import { MONTHS_LOOKBACK } from "../constants";

// Reglas puras del módulo preinvoice: cálculo del rango de períodos disponibles
// y validaciones. Sin React, sin Swal, sin HTTP. La fecha "ahora" se inyecta para
// poder testear de forma determinística.

/**
 * Lista de meses/años disponibles para generar una prefactura: los últimos
 * `monthsBack` meses contando hacia atrás desde hoy. Devuelve además el mes/año
 * por defecto (el actual).
 *
 * @param {string[]} months - nombres de los 12 meses ya traducidos (índice 0 = enero).
 * @param {number} [monthsBack] - cuántos meses hacia atrás ofrecer.
 * @param {Date} [now] - fecha de referencia.
 */
/** Item del selector de periodo. `year` es numerico; ver la nota de filterMonthsByYear. */
export interface AvailableMonth {
  month: string;
  year: number;
  monthIndex: number;
}

export const getAvailableYearsAndMonths = (
  months: string[],
  monthsBack: number = MONTHS_LOOKBACK,
  now: Date = new Date()
) => {
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  const availableMonths: AvailableMonth[] = [];
  const availableYears = new Set<string>();

  for (let i = 0; i < monthsBack; i++) {
    const targetDate = new Date(currentYear, currentMonth - i);
    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();

    // `month` es 0-11 y `months` trae los 12 nombres (ver JSDoc).
    availableMonths.push({ month: months[month]!, year, monthIndex: month });
    availableYears.add(year.toString());
  }

  availableMonths.sort((a, b) => {
    if (a.year !== b.year) return parseInt(String(b.year)) - parseInt(String(a.year));
    return a.monthIndex - b.monthIndex;
  });

  return {
    availableMonths,
    years: Array.from(availableYears).sort((a, b) => b.localeCompare(a)),
    defaultMonth: months[currentMonth]!,
    defaultYear: currentYear.toString(),
  };
};

/**
 * Meses disponibles de un año puntual + el mes por defecto (el último de la lista).
 * El match normaliza a número a proposito: el select de Año entrega texto, mientras
 * que `year` es numérico. Comparar sin normalizar dejaba el selector de Mes vacío.
 */
export const filterMonthsByYear = (availableMonths: AvailableMonth[], selectedYear: number | string) => {
  const year = Number(selectedYear);
  const months = availableMonths
    .filter((item) => Number(item.year) === year)
    .map((item) => item.month);

  return { months, defaultMonth: months[months.length - 1] };
};

/** El período (mes 1-based, año) seleccionado cae en el futuro respecto de hoy. */
export const isFutureMonthYear = (month: number, year: number, now: Date = new Date()) =>
  new Date(year, month - 1) > now;

/**
 * Parámetros i18n del texto "del mes X de A al mes Y de B" que describe el rango
 * de períodos ofrecido (desde `monthsBack - 1` meses atrás hasta el mes actual).
 */
export const monthRangeParams = (months: string[], monthsBack: number = MONTHS_LOOKBACK, now: Date = new Date()) => {
  const start = new Date(now.getFullYear(), now.getMonth() - (monthsBack - 1));
  return {
    startMonth: months[start.getMonth()],
    currentMonth: months[now.getMonth()],
    startYear: start.getFullYear(),
    currentYear: now.getFullYear(),
  };
};

/** Total de una línea de detalle: unidades por precio unitario. */
/** Acepta strings a proposito: los inputs entregan texto y la coercion esta cubierta por test. */
export const computeLineTotal = (units: number | string, priceUnit: number | string): number =>
  Number(units) * Number(priceUnit);
